import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import Toast from './Toast';
import { setToastInstance } from './index';
import { mergeConfig, TOAST_TYPES } from './toastConfig';
import './Toast.scss';

const ToastContext = createContext(null);
let toastIdCounter = 0;

export const ToastProvider = ({ children, config = {} }) => {
  const [toasts, setToasts] = useState([]);
  const isMountedRef = useRef(true);
  const toastConfig = mergeConfig(config);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      setToasts([]);
      setToastInstance(null);
    };
  }, []);

  const showToast = useCallback((message, type = TOAST_TYPES.INFO, options = {}) => {
    if (!isMountedRef.current || !message) return null;

    const id = `toast-${++toastIdCounter}`;
    // Use custom duration if provided, otherwise use default from config
    const duration = options.duration !== undefined 
      ? Number(options.duration)
      : (toastConfig.durations[type] || toastConfig.defaultDuration);
    
    const toast = {
      id,
      message,
      type: type || TOAST_TYPES.INFO,
      duration,
      className: options.className || '',
      style: options.style || {},
      showIcon: options.showIcon !== undefined ? options.showIcon : toastConfig.showIcon,
      showProgressBar: options.showProgressBar !== undefined ? options.showProgressBar : toastConfig.showProgressBar,
      clickToClose: options.clickToClose !== undefined ? options.clickToClose : toastConfig.clickToClose,
      icon: options.icon || toastConfig.icons[type],
      config: toastConfig,
    };

    setToasts((prevToasts) => {
      if (!isMountedRef.current) return prevToasts;
      const newToasts = [...prevToasts, toast];
      return newToasts.slice(-toastConfig.maxToasts);
    });

    return id;
  }, [toastConfig]);

  const removeToast = useCallback((id) => {
    if (!isMountedRef.current || !id) return;
    setToasts((prevToasts) => prevToasts.filter((toast) => toast?.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    if (!isMountedRef.current) return;
    setToasts([]);
  }, []);

  // Create type-specific methods
  const success = useCallback((message, options) => showToast(message, TOAST_TYPES.SUCCESS, options), [showToast]);
  const error = useCallback((message, options) => showToast(message, TOAST_TYPES.ERROR, options), [showToast]);
  const warning = useCallback((message, options) => showToast(message, TOAST_TYPES.WARNING, options), [showToast]);
  const info = useCallback((message, options) => showToast(message, TOAST_TYPES.INFO, options), [showToast]);

  const value = {
    showToast,
    success,
    error,
    warning,
    info,
    removeToast,
    clearAllToasts,
    config: toastConfig,
  };

  // Set instance for direct toast import
  useEffect(() => {
    if (isMountedRef.current) {
      setToastInstance(value);
    }
    return () => setToastInstance(null);
  }, [value]);

  // Get position styles
  const getPositionStyles = () => {
    const position = toastConfig.position || 'top-right';
    const baseStyle = { zIndex: 10000, position: 'fixed', pointerEvents: 'none' };
    
    const positions = {
      'top-right': { ...baseStyle, top: '20px', right: '20px' },
      'top-left': { ...baseStyle, top: '20px', left: '20px' },
      'top-center': { ...baseStyle, top: '20px', left: '50%', transform: 'translateX(-50%)' },
      'bottom-right': { ...baseStyle, bottom: '20px', right: '20px' },
      'bottom-left': { ...baseStyle, bottom: '20px', left: '20px' },
      'bottom-center': { ...baseStyle, bottom: '20px', left: '50%', transform: 'translateX(-50%)' },
    };
    
    return positions[position] || positions['top-right'];
  };

  const validToasts = toasts.filter(toast => toast?.id);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {validToasts.length > 0 && (
        <div 
          className={`kn-toast-container ${toastConfig.position || 'top-right'}`}
          style={{ ...toastConfig.containerStyles, ...getPositionStyles() }}
        >
          {validToasts.map((toast) => (
            <Toast
              key={toast.id}
              id={toast.id}
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              onClose={removeToast}
              className={toast.className}
              style={toast.style}
              showIcon={toast.showIcon}
              showProgressBar={toast.showProgressBar}
              clickToClose={toast.clickToClose}
              icon={toast.icon}
              config={toast.config}
            />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};

export default ToastContext;
